import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, Chip,
  ToggleButtonGroup, ToggleButton, Divider, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Link as LinkIcon, CheckCircle, ContentCopy, QrCode2, Api } from '@mui/icons-material';
import { api } from '../config/api';
import { useAgentes } from '../context/AgenteContext';

const Conexion: React.FC = () => {
  const { agentes, actual: agenteActual } = useAgentes();
  const [cred, setCred] = useState<any>({ modo: 'api', api_version: 'v21.0' });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [msg, setMsg] = useState('');
  const [verificando, setVerificando] = useState(false);
  // Estado del modo QR (Baileys)
  const [qrEstado, setQrEstado] = useState<{ conectado: boolean; qr: string | null; numero: string | null }>({ conectado: false, qr: null, numero: null });
  const [qrCargando, setQrCargando] = useState(false);
  const pollRef = useRef<any>(null);

  const consultarQr = async () => {
    try {
      const { data } = await api.get('/meta/qr/estado');
      setQrEstado(data);
      if (data.conectado && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch { /* ignore */ }
  };

  const generarQr = async () => {
    setQrCargando(true);
    try {
      await api.post('/meta/qr/iniciar');
      // Poll cada 2s hasta tener QR o conexión
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(consultarQr, 2000);
      setTimeout(consultarQr, 1500);
    } finally { setQrCargando(false); }
  };

  const cerrarQr = async () => {
    await api.post('/meta/qr/cerrar');
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setQrEstado({ conectado: false, qr: null, numero: null });
  };

  // Al entrar en modo QR, consultar estado; limpiar polling al salir.
  useEffect(() => {
    if (cred.modo === 'qr') { consultarQr(); }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [cred.modo]);

  const cargar = () => {
    api.get('/meta/credenciales').then((r) => {
      const c = r.data.credenciales;
      setWebhookUrl(r.data.webhookUrl);
      if (c) {
        setCred({ modo: c.modo || 'api', phone_number_id: c.phone_number_id || '', waba_id: c.waba_id || '', api_version: c.api_version || 'v21.0', verificado: c.verificado, numero_display: c.numero_display, tiene_token: c.tiene_token });
        setVerifyToken(c.verify_token || '');
      }
    });
  };
  useEffect(cargar, []);

  const set = (k: string) => (e: any) => setCred({ ...cred, [k]: e.target.value });

  const guardar = async () => {
    setMsg('');
    await api.put('/meta/credenciales', cred);
    setMsg('Credenciales guardadas');
    cargar();
  };

  const verificar = async () => {
    setVerificando(true);
    setMsg('');
    try {
      const { data } = await api.post('/meta/credenciales/verificar');
      if (data.ok) { setMsg(`Conectado a ${data.numero || 'tu número'}`); cargar(); }
      else setMsg(`Error: ${data.error}`);
    } finally {
      setVerificando(false);
    }
  };

  const copiar = (t: string) => navigator.clipboard.writeText(t);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <LinkIcon color="primary" />
        <Typography variant="h4">Conexión WhatsApp</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Conecta tu WhatsApp Business con tus credenciales de Meta. Son 100% tuyas y privadas.
      </Typography>

      {agenteActual && agentes.length > 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Estás conectando el número del agente <strong>{agenteActual.nombre}</strong>. Cada agente usa un número distinto; cambia de agente en la barra superior.
        </Alert>
      )}

      {msg && <Alert severity={msg.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>{msg}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <ToggleButtonGroup exclusive value={cred.modo} onChange={(_, v) => v && setCred({ ...cred, modo: v })} sx={{ mb: 2 }}>
            <ToggleButton value="api"><Api sx={{ mr: 1 }} /> API oficial</ToggleButton>
            <ToggleButton value="qr"><QrCode2 sx={{ mr: 1 }} /> QR (test)</ToggleButton>
          </ToggleButtonGroup>

          {cred.modo === 'api' ? (
            <Stack spacing={2}>
              <TextField label="Access Token de Meta" fullWidth type="password"
                placeholder={cred.tiene_token ? '•••••••• (guardado, escribe para cambiar)' : 'EAAG...'}
                onChange={set('access_token')} />
              <TextField label="Phone Number ID" fullWidth value={cred.phone_number_id || ''} onChange={set('phone_number_id')} />
              <TextField label="WABA ID (WhatsApp Business Account ID)" fullWidth value={cred.waba_id || ''} onChange={set('waba_id')} />
              <TextField label="Versión API" fullWidth value={cred.api_version || 'v21.0'} onChange={set('api_version')} />
              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={guardar}>Guardar</Button>
                <Button variant="outlined" onClick={verificar} disabled={verificando}>
                  {verificando ? 'Verificando...' : 'Verificar conexión'}
                </Button>
              </Stack>
              {cred.verificado && (
                <Chip icon={<CheckCircle />} color="success" label={`Verificado: ${cred.numero_display || 'número conectado'}`} />
              )}
            </Stack>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                El modo QR (WhatsApp Web) es para pruebas rápidas sin API oficial. Haz clic en "Generar QR",
                escanéalo desde tu teléfono (WhatsApp → Dispositivos vinculados) y tu agente queda conectado.
                Para producción recomendamos la API oficial de Meta.
              </Alert>

              {qrEstado.conectado ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                  <Chip icon={<CheckCircle />} color="success" label={`Conectado: +${qrEstado.numero || ''}`} sx={{ fontSize: 16, py: 2 }} />
                  <Typography variant="body2" color="text.secondary">Tu WhatsApp está vinculado. El agente ya responde los mensajes entrantes.</Typography>
                  <Button variant="outlined" color="error" onClick={cerrarQr}>Desvincular</Button>
                </Stack>
              ) : qrEstado.qr ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <Typography variant="subtitle1">Escanea este código con tu WhatsApp</Typography>
                  <Box component="img" src={qrEstado.qr} alt="QR WhatsApp" sx={{ width: 260, height: 260, border: '1px solid #e2e8f0', borderRadius: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    WhatsApp → Ajustes → Dispositivos vinculados → Vincular dispositivo
                  </Typography>
                  <Button size="small" onClick={cerrarQr}>Cancelar</Button>
                </Stack>
              ) : (
                <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                  <QrCode2 sx={{ fontSize: 56, color: 'text.secondary' }} />
                  <Button variant="contained" startIcon={<QrCode2 />} onClick={generarQr} disabled={qrCargando}>
                    {qrCargando ? 'Generando...' : 'Generar QR'}
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Webhook de Meta</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En tu app de Meta, configura el webhook con esta URL y este token de verificación.
          </Typography>
          <TextField label="Callback URL" fullWidth sx={{ mb: 2 }} value={webhookUrl} InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end"><Tooltip title="Copiar"><IconButton onClick={() => copiar(webhookUrl)}><ContentCopy fontSize="small" /></IconButton></Tooltip></InputAdornment>,
          }} />
          <TextField label="Verify Token" fullWidth value={verifyToken} InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end"><Tooltip title="Copiar"><IconButton onClick={() => copiar(verifyToken)}><ContentCopy fontSize="small" /></IconButton></Tooltip></InputAdornment>,
          }} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Suscribe el webhook al campo <strong>messages</strong>.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Conexion;
